import type {
    CreateSavedChecklistInput,
    ListSavedChecklistsResponse,
    SavedChecklist,
    SavedChecklistErrorResponse,
    SavedChecklistResponse,
    UpdateSavedChecklistInput,
} from '@repo/map-user-data/types';

const OVERRIDE_URL = process.env.NEXT_PUBLIC_MAP_USER_DATA_URL;
const BASE = '/maps/api/user-data';

function authHeaders(token: string): HeadersInit {
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function buildUrl(mapExternalId: string, path: string): string {
    if (OVERRIDE_URL) {
        const params = new URLSearchParams({ mapExternalId });
        return `${OVERRIDE_URL.replace(/\/$/, '')}${path}?${params.toString()}`;
    }
    return `${BASE}/${encodeURIComponent(mapExternalId)}${path}`;
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    const body = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
        const err = body as SavedChecklistErrorResponse | null;
        throw new Error(err?.error?.message ?? `request failed with ${response.status}`);
    }
    return body as T;
}

export async function listSavedChecklists(mapExternalId: string, token: string): Promise<SavedChecklist[]> {
    const response = await fetch(buildUrl(mapExternalId, '/checklists'), {
        headers: authHeaders(token),
    });
    const body = await parseResponse<ListSavedChecklistsResponse>(response);
    return body.checklists;
}

export async function createSavedChecklist(
    mapExternalId: string,
    input: CreateSavedChecklistInput,
    token: string,
): Promise<SavedChecklist> {
    const response = await fetch(buildUrl(mapExternalId, '/checklists'), {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(input),
    });
    const body = await parseResponse<SavedChecklistResponse>(response);
    return body.checklist;
}

export async function updateSavedChecklist(
    mapExternalId: string,
    id: string,
    input: UpdateSavedChecklistInput,
    token: string,
): Promise<SavedChecklist> {
    const response = await fetch(buildUrl(mapExternalId, `/checklists/${encodeURIComponent(id)}`), {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(input),
    });
    const body = await parseResponse<SavedChecklistResponse>(response);
    return body.checklist;
}

export async function deleteSavedChecklist(mapExternalId: string, id: string, token: string): Promise<void> {
    const response = await fetch(buildUrl(mapExternalId, `/checklists/${encodeURIComponent(id)}`), {
        method: 'DELETE',
        headers: authHeaders(token),
    });
    await parseResponse<void>(response);
}
